import React, { useState, useEffect, useRef } from 'react'
import {
  Card, Row, Col, Statistic, Badge, Progress, Alert, Space, Button, Switch, message
} from 'antd'
import { 
  ReloadOutlined, PauseCircleOutlined, PlayCircleOutlined, 
  ApiOutlined, DatabaseOutlined, ClockCircleOutlined, 
  CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons'
import { conversationApi } from '@/services/api'
import ReactECharts from 'echarts-for-react'
import dayjs from 'dayjs'

interface RealTimeStats {
  api_status: 'healthy' | 'warning' | 'error'
  total_requests_last_hour: number
  active_sessions: number
  avg_response_time: number
  error_rate: number
  database_connections: number
  memory_usage: number
  cpu_usage: number
  disk_usage: number
}

interface RequestMetrics {
  timestamp: number
  requests_per_minute: number
  response_time: number
  error_count: number
  success_count: number
}

interface SystemAlert {
  id: string
  type: 'error' | 'warning' | 'info'
  message: string
  timestamp: number
  resolved: boolean
}

const RealTimeMonitorPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval] = useState(5000) // 5秒
  const [realTimeStats, setRealTimeStats] = useState<RealTimeStats>({
    api_status: 'healthy',
    total_requests_last_hour: 0,
    active_sessions: 0,
    avg_response_time: 0,
    error_rate: 0,
    database_connections: 0,
    memory_usage: 0,
    cpu_usage: 0,
    disk_usage: 0
  })
  const [requestMetrics, setRequestMetrics] = useState<RequestMetrics[]>([])
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([])
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const metricsHistoryRef = useRef<RequestMetrics[]>([])

  useEffect(() => {
    fetchData()
    
    if (autoRefresh) {
      startAutoRefresh()
    } else {
      stopAutoRefresh()
    }

    return () => {
      stopAutoRefresh()
    }
  }, [autoRefresh, refreshInterval])

  const startAutoRefresh = () => {
    stopAutoRefresh()
    intervalRef.current = setInterval(() => {
      fetchData()
    }, refreshInterval)
  }

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // 模拟获取实时监控数据
      const now = Date.now()
      
      // 获取基础统计数据
      const [, ] = await Promise.all([
        conversationApi.getStats(),
        conversationApi.getClientUsageStats()
      ])

      // 模拟实时系统状态
      const mockStats: RealTimeStats = {
        api_status: Math.random() > 0.1 ? 'healthy' : (Math.random() > 0.5 ? 'warning' : 'error'),
        total_requests_last_hour: Math.floor(Math.random() * 1000) + 500,
        active_sessions: Math.floor(Math.random() * 50) + 10,
        avg_response_time: Math.floor(Math.random() * 200) + 50,
        error_rate: Math.random() * 5,
        database_connections: Math.floor(Math.random() * 10) + 5,
        memory_usage: Math.random() * 30 + 40,
        cpu_usage: Math.random() * 20 + 10,
        disk_usage: Math.random() * 10 + 60
      }

      setRealTimeStats(mockStats)

      // 更新请求指标历史
      const newMetric: RequestMetrics = {
        timestamp: now,
        requests_per_minute: Math.floor(Math.random() * 100) + 20,
        response_time: mockStats.avg_response_time,
        error_count: Math.floor(Math.random() * 5),
        success_count: Math.floor(Math.random() * 95) + 50
      }

      metricsHistoryRef.current = [...metricsHistoryRef.current, newMetric].slice(-60) // 保留最近60个数据点
      setRequestMetrics([...metricsHistoryRef.current])

      // 生成系统告警（模拟）
      if (mockStats.error_rate > 3 || mockStats.avg_response_time > 200) {
        const alertId = `alert-${now}`
        const newAlert: SystemAlert = {
          id: alertId,
          type: mockStats.error_rate > 3 ? 'error' : 'warning',
          message: mockStats.error_rate > 3 
            ? `错误率过高: ${mockStats.error_rate.toFixed(2)}%` 
            : `响应时间过长: ${mockStats.avg_response_time}ms`,
          timestamp: now,
          resolved: false
        }

        setSystemAlerts(prev => [newAlert, ...prev.slice(0, 9)]) // 保留最近10条告警
      }

    } catch (error) {
      console.error('获取监控数据失败:', error)
      message.error('获取监控数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 请求趋势图表配置
  const getRequestTrendChart = () => ({
    title: {
      text: '实时请求趋势',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['请求/分钟', '响应时间(ms)', '错误数'],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: requestMetrics.map(m => dayjs(m.timestamp).format('HH:mm:ss')),
      axisLabel: {
        rotate: 45
      }
    },
    yAxis: [
      {
        type: 'value',
        name: '请求数',
        position: 'left'
      },
      {
        type: 'value',
        name: '响应时间/错误数',
        position: 'right'
      }
    ],
    series: [
      {
        name: '请求/分钟',
        type: 'line',
        data: requestMetrics.map(m => m.requests_per_minute),
        smooth: true,
        itemStyle: { color: '#1890ff' }
      },
      {
        name: '响应时间(ms)',
        type: 'line',
        yAxisIndex: 1,
        data: requestMetrics.map(m => m.response_time),
        smooth: true,
        itemStyle: { color: '#52c41a' }
      },
      {
        name: '错误数',
        type: 'bar',
        yAxisIndex: 1,
        data: requestMetrics.map(m => m.error_count),
        itemStyle: { color: '#ff4d4f' }
      }
    ]
  })



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success'
      case 'warning': return 'warning'
      case 'error': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleOutlined />
      case 'warning': return <ExclamationCircleOutlined />
      case 'error': return <CloseCircleOutlined />
      default: return <ClockCircleOutlined />
    }
  }

  const resolveAlert = (alertId: string) => {
    setSystemAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId ? { ...alert, resolved: true } : alert
      )
    )
    message.success('告警已标记为已解决')
  }

  return (
    <div style={{ padding: '0' }}>
      <Card title="实时监控" extra={
        <Space>
          <span>自动刷新:</span>
          <Switch 
            checked={autoRefresh} 
            onChange={setAutoRefresh}
            checkedChildren="开"
            unCheckedChildren="关"
          />
          <Button 
            icon={autoRefresh ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '暂停' : '开始'}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            刷新
          </Button>
        </Space>
      }>
        {/* 系统状态概览 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="API状态"
                value={realTimeStats.api_status}
                prefix={getStatusIcon(realTimeStats.api_status)}
                valueStyle={{ 
                  color: realTimeStats.api_status === 'healthy' ? '#52c41a' : 
                         realTimeStats.api_status === 'warning' ? '#faad14' : '#ff4d4f'
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="活跃会话"
                value={realTimeStats.active_sessions}
                prefix={<ApiOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="平均响应时间"
                value={realTimeStats.avg_response_time}
                suffix="ms"
                prefix={<ClockCircleOutlined />}
                valueStyle={{ 
                  color: realTimeStats.avg_response_time > 200 ? '#ff4d4f' : '#52c41a'
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="错误率"
                value={realTimeStats.error_rate.toFixed(2)}
                suffix="%"
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ 
                  color: realTimeStats.error_rate > 3 ? '#ff4d4f' : '#52c41a'
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* 详细指标 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Statistic
              title="小时请求数"
              value={realTimeStats.total_requests_last_hour}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="数据库连接"
              value={realTimeStats.database_connections}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Col>
          <Col span={4}>
            <div>
              <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>CPU使用率</div>
              <Progress
                percent={Number(realTimeStats.cpu_usage.toFixed(2))}
                status={realTimeStats.cpu_usage > 80 ? 'exception' : 'normal'}
                format={percent => `${(percent || 0).toFixed(2)}%`}
                size={[200, 8]}
                style={{ marginBottom: 4 }}
              />
            </div>
          </Col>
          <Col span={4}>
            <div>
              <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>内存使用率</div>
              <Progress
                percent={Number(realTimeStats.memory_usage.toFixed(2))}
                status={realTimeStats.memory_usage > 80 ? 'exception' : 'normal'}
                format={percent => `${(percent || 0).toFixed(2)}%`}
                size={[200, 8]}
                style={{ marginBottom: 4 }}
              />
            </div>
          </Col>
          <Col span={4}>
            <div>
              <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>磁盘使用率</div>
              <Progress
                percent={Number(realTimeStats.disk_usage.toFixed(2))}
                status={realTimeStats.disk_usage > 90 ? 'exception' : 'normal'}
                format={percent => `${(percent || 0).toFixed(2)}%`}
                size={[200, 8]}
                style={{ marginBottom: 4 }}
              />
            </div>
          </Col>
          <Col span={4}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 8 }}>系统状态</div>
              <Badge 
                status={getStatusColor(realTimeStats.api_status)} 
                text={realTimeStats.api_status.toUpperCase()}
              />
            </div>
          </Col>
        </Row>

        {/* 图表展示 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={16}>
            <Card title="实时请求趋势" size="small">
              <ReactECharts
                option={getRequestTrendChart()}
                style={{ width: '100%', height: '300px' }}
                notMerge={true}
                lazyUpdate={true}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card title="系统资源监控" size="small">
              <Row gutter={[8, 16]}>
                <Col span={24}>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: 8 }}>CPU使用率</div>
                    <Progress
                      type="circle"
                      percent={Number(realTimeStats.cpu_usage.toFixed(2))}
                      format={percent => `${(percent || 0).toFixed(2)}%`}
                      status={realTimeStats.cpu_usage > 80 ? 'exception' : 'normal'}
                      size={80}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 8 }}>内存</div>
                    <Progress
                      type="circle"
                      percent={Number(realTimeStats.memory_usage.toFixed(2))}
                      format={percent => `${(percent || 0).toFixed(2)}%`}
                      status={realTimeStats.memory_usage > 80 ? 'exception' : 'normal'}
                      size={60}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 8 }}>磁盘</div>
                    <Progress
                      type="circle"
                      percent={Number(realTimeStats.disk_usage.toFixed(2))}
                      format={percent => `${(percent || 0).toFixed(2)}%`}
                      status={realTimeStats.disk_usage > 90 ? 'exception' : 'normal'}
                      size={60}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* 系统告警 */}
        <Card title="系统告警" size="small">
          {systemAlerts.length === 0 ? (
            <Alert
              message="系统运行正常"
              description="当前没有系统告警"
              type="success"
              showIcon
            />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {systemAlerts.map(alert => (
                <Alert
                  key={alert.id}
                  message={alert.message}
                  description={`时间: ${dayjs(alert.timestamp).format('YYYY-MM-DD HH:mm:ss')}`}
                  type={alert.type}
                  showIcon
                  closable={!alert.resolved}
                  onClose={() => resolveAlert(alert.id)}
                  style={{ 
                    opacity: alert.resolved ? 0.6 : 1,
                    textDecoration: alert.resolved ? 'line-through' : 'none'
                  }}
                />
              ))}
            </Space>
          )}
        </Card>
      </Card>
    </div>
  )
}

export default RealTimeMonitorPage
